import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Button } from '../Button';
import { ReactMailQuill } from './ReactMailQuill';
import { CURRENT_USER } from '../../constants';
import mailClientAPI from '../../api/mail-client-api';
import { notification, Checkbox } from 'antd';

// Helper function to convert timestamp (integer) to datetime-local format
const timestampToDatetimeLocal = (timestamp) => {
  if (!timestamp) return '';
  
  // If it's already a datetime-local string, return as is (for internal state)
  if (typeof timestamp === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(timestamp)) {
    return timestamp;
  }
  
  // Convert integer timestamp (milliseconds) to datetime-local format
  try {
    const date = new Date(Number(timestamp));
    if (isNaN(date.getTime())) return '';
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  } catch (e) {
    return '';
  }
};

// Helper function to convert datetime-local format to timestamp (integer)
const datetimeLocalToTimestamp = (datetimeLocal) => {
  if (!datetimeLocal) return null;
  
  // If it's already a timestamp, return as is
  if (typeof datetimeLocal === 'number') {
    return datetimeLocal;
  }
  
  // Convert datetime-local string to timestamp
  try {
    const date = new Date(datetimeLocal);
    if (isNaN(date.getTime())) return null;
    return date.getTime(); // Returns milliseconds since epoch
  } catch (e) {
    return null;
  }
};

export const Settings = ({ onClose, userInfo, mailboxes: mailboxesProp = [] }) => {
  const mailboxes = mailboxesProp;
  // Use userInfo from props if available, otherwise fall back to CURRENT_USER
  const email = userInfo?.email || CURRENT_USER.email;
  const avatar = userInfo?.avatar || CURRENT_USER.avatar;
  
  // State for form fields
  const [displayName, setDisplayName] = useState(userInfo?.name || CURRENT_USER.name || '');
  const [signature, setSignature] = useState(userInfo?.signature || '');
  const [loading, setLoading] = useState(false);
  const [showFormattingToolbar, setShowFormattingToolbar] = useState(false);
  
  // Refs
  const quillRef = useRef(null);
  
  // Out of Office State
  // Store as datetime-local strings for the input fields, convert to timestamps when saving
  const [outOfOfficeEnabled, setOutOfOfficeEnabled] = useState(userInfo?.outOfOffice?.enabled || false);
  const [outOfOfficeStartDate, setOutOfOfficeStartDate] = useState(timestampToDatetimeLocal(userInfo?.outOfOffice?.startDate) || '');
  const [outOfOfficeEndDate, setOutOfOfficeEndDate] = useState(timestampToDatetimeLocal(userInfo?.outOfOffice?.endDate) || '');
  const [outOfOfficeMessage, setOutOfOfficeMessage] = useState(userInfo?.outOfOffice?.message || '');

  // Modal State
  const [activeModal, setActiveModal] = useState(null); // 'export' | 'import' | null
  const [step, setStep] = useState(1);
  const [isProcessing, setIsProcessing] = useState(false);

  // Export State
  const [selectedFolders, setSelectedFolders] = useState([]);
  const [dateRange, setDateRange] = useState('all');
  const [exportEmail, setExportEmail] = useState(email);
  
  // Initialize selected folders when mailboxes are loaded
  useEffect(() => {
    if (mailboxes.length > 0 && selectedFolders.length === 0) {
      setSelectedFolders(mailboxes.map(m => m.path));
    }
  }, [mailboxes, selectedFolders.length]);

  // Import State
  const [importFile, setImportFile] = useState(null);
  const [importStrategy, setImportStrategy] = useState('merge');

  // Update state when userInfo changes
  useEffect(() => {
    if (userInfo) {
      setDisplayName(userInfo.name || userInfo.email?.split("@")[0] || '');
      setSignature(userInfo.signature || '');
      setExportEmail(userInfo.email || email);
      if (userInfo.outOfOffice) {
        setOutOfOfficeEnabled(userInfo.outOfOffice.enabled || false);
        // Convert timestamps to datetime-local format for inputs
        setOutOfOfficeStartDate(timestampToDatetimeLocal(userInfo.outOfOffice.startDate) || '');
        setOutOfOfficeEndDate(timestampToDatetimeLocal(userInfo.outOfOffice.endDate) || '');
        setOutOfOfficeMessage(userInfo.outOfOffice.message || '');
      } else {
        // Reset out of office fields if not present
        setOutOfOfficeEnabled(false);
        setOutOfOfficeStartDate('');
        setOutOfOfficeEndDate('');
        setOutOfOfficeMessage('');
      }
    }
  }, [userInfo, email]);

  // Quill modules configuration for custom toolbar
  const modules = useMemo(() => {
    return {
      toolbar: {
        container: "#rich-area-text-effect-settings",
      },
    };
  }, []);

  const handleSave = async () => {
    // Validate out-of-office dates before saving
    if (outOfOfficeEnabled) {
      const startTimestamp = datetimeLocalToTimestamp(outOfOfficeStartDate);
      const endTimestamp = datetimeLocalToTimestamp(outOfOfficeEndDate);
      const now = Date.now();

      if (!startTimestamp || !endTimestamp) {
        notification.error({
          message: 'Invalid dates',
          description: 'Please provide both start and end dates for out-of-office.',
          placement: 'bottomLeft',
        });
        setLoading(false);
        return;
      }

      if (startTimestamp < now) {
        notification.error({
          message: 'Invalid start date',
          description: 'Start date cannot be in the past.',
          placement: 'bottomLeft',
        });
        setLoading(false);
        return;
      }

      if (endTimestamp <= startTimestamp) {
        notification.error({
          message: 'Invalid end date',
          description: 'End date must be after start date.',
          placement: 'bottomLeft',
        });
        setLoading(false);
        return;
      }
    }

    setLoading(true);
    try {
      // Convert datetime-local strings to timestamps (integers) before sending
      const outOfOffice = {
        enabled: outOfOfficeEnabled,
        startDate: datetimeLocalToTimestamp(outOfOfficeStartDate),
        endDate: datetimeLocalToTimestamp(outOfOfficeEndDate),
        message: outOfOfficeMessage || '',
      };
      await mailClientAPI.updateSettings(displayName, signature, outOfOffice);
      notification.success({
        message: 'Settings saved',
        description: 'Your settings have been saved successfully.',
        placement: 'bottomLeft',
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      notification.error({
        message: 'Failed to save settings',
        description: error.response?.data?.Output?.message || 'An error occurred while saving your settings.',
        placement: 'bottomLeft',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setActiveModal(null);
    setStep(1);
    setIsProcessing(false);
    setImportFile(null);
    if (mailboxes.length > 0) {
      setSelectedFolders(mailboxes.map(m => m.path));
    }
    setExportEmail(email);
  };

  const toggleFolder = (path) => {
    if (selectedFolders.includes(path)) {
      setSelectedFolders(selectedFolders.filter(f => f !== path));
    } else {
      setSelectedFolders([...selectedFolders, path]);
    }
  };

  // Get folder icon based on path
  const getFolderIcon = (path) => {
    const lowerPath = path.toLowerCase();
    if (lowerPath === 'inbox' || lowerPath.includes('inbox')) return 'inbox';
    if (lowerPath.includes('sent')) return 'send';
    if (lowerPath.includes('draft')) return 'drafts';
    if (lowerPath.includes('archive')) return 'archive';
    if (lowerPath.includes('junk') || lowerPath.includes('spam')) return 'report';
    if (lowerPath.includes('trash')) return 'delete';
    return 'folder';
  };

  // Get folder display name
  const getFolderName = (path) => {
    const lowerPath = path.toLowerCase();
    if (lowerPath === 'inbox' || lowerPath.includes('inbox')) return 'Inbox';
    if (lowerPath.includes('sent')) return 'Sent';
    if (lowerPath.includes('draft')) return 'Drafts';
    if (lowerPath.includes('archive')) return 'Archived';
    if (lowerPath.includes('junk') || lowerPath.includes('spam')) return 'Spam';
    if (lowerPath.includes('trash')) return 'Trash';
    // Capitalize first letter of mailbox name
    const parts = path.split(/[./]/);
    const name = parts[parts.length - 1] || path;
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  };

  // Calculate date filter
  const getDateFilter = () => {
    const now = new Date();
    switch (dateRange) {
      case 'last_year':
        return new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
      case 'last_month':
        return new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
      default:
        return null; // All time
    }
  };

  // Export to JSON
  const handleExport = async () => {
    setIsProcessing(true);
    try {
      // Call backend export endpoint
      const response = await mailClientAPI.exportEmails(selectedFolders, dateRange, exportEmail);
      
      setIsProcessing(false);
      setStep(3);
      
      notification.success({
        message: 'Export Initiated',
        description: response.Output?.message || 'Your email export has been initiated. You will receive an email with the download link when it\'s ready.',
        placement: 'bottomLeft',
      });
    } catch (error) {
      console.error('Error initiating email export:', error);
      setIsProcessing(false);
      notification.error({
        message: 'Export Failed',
        description: error.response?.data?.Output?.message || 'An error occurred while initiating the export.',
        placement: 'bottomLeft',
      });
    }
  };

  // Import from JSON or MBOX
  const handleImport = async () => {
    if (!importFile) return;

    setIsProcessing(true);
    try {
      const fileExtension = importFile.name.split('.').pop().toLowerCase();
      
      if (fileExtension !== 'json' && fileExtension !== 'mbox') {
        throw new Error('Unsupported file format. Please use .json or .mbox files.');
      }

      // Call backend import endpoint
      const response = await mailClientAPI.importEmails(importFile, importStrategy);
      
      setIsProcessing(false);
      setStep(3);
      
      notification.success({
        message: 'Import Initiated',
        description: response.Output?.message || 'Your email import has been initiated. Processing in the background.',
        placement: 'bottomLeft',
      });
    } catch (error) {
      console.error('Error importing emails:', error);
      setIsProcessing(false);
      notification.error({
        message: 'Import Failed',
        description: error.response?.data?.Output?.message || error.message || 'An error occurred while importing your emails.',
        placement: 'bottomLeft',
      });
    }
  };

  const handleProcessAction = () => {
    if (activeModal === 'export') {
      handleExport();
    } else if (activeModal === 'import') {
      handleImport();
    }
  };

  const currentDisplayName = displayName || email?.split("@")[0] || '';

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="flex items-center px-6 py-4 border-b border-gray-100">
        <button onClick={onClose} className="flex mr-4 p-2 hover:bg-gray-100 rounded-full text-gray-500">
           <span className="material-icons-outlined">arrow_back</span>
        </button>
        <h2 className="text-xl font-semibold text-gray-800">Inbox Settings</h2>
      </div>
      <div className="flex-1 overflow-y-auto p-8 w-full">
        <div className="max-w-4xl mx-auto w-full">
          <div className="mb-10 pb-10 border-b border-gray-100">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Profile & Account</h3>
            <div className="flex items-start gap-6">
                  {avatar ? (
                    <img src={avatar} alt="Profile" className="w-20 h-20 rounded-full bg-gray-200 object-cover" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold text-2xl">
                      {currentDisplayName.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1">
                      <div className="grid grid-cols-2 gap-6">
                          <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
                              <input 
                                type="text" 
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-grigora-500 outline-none" 
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                placeholder="Enter your display name"
                              />
                          </div>
                          <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                              <input type="text" disabled className="w-full border border-gray-200 bg-gray-50 text-gray-500 rounded-lg px-3 py-2 text-sm outline-none" value={email} />
                          </div>
                      </div>
                  </div>
            </div>
          </div>
          <div className="mb-10 pb-10 border-b border-gray-100">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Email Signature</h3>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="bg-white rounded-lg overflow-hidden">
                    <div className="flex-1 overflow-hidden relative flex flex-col">
                      <div className={`flex`}>
                        <div id="rich-area-text-effect-settings" className={`quill-toolbar-wrapper`}>
                          <span className="ql-formats">
                            <select className="ql-font"></select>
                            <button className="ql-bold"></button>
                            <button className="ql-italic"></button>
                            <button className="ql-underline"></button>
                            <button className="ql-color"></button>
                            <select className="ql-align"></select>
                            <button className="ql-list" value="ordered"></button>
                            <button className="ql-list" value="bullet"></button>
                            <button className="ql-blockquote"></button>
                            <button className="ql-link"></button>
                            <button className="ql-image"></button>
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 overflow-y-auto p-4 h-full flex flex-col">
                        <ReactMailQuill
                          ref={quillRef}
                          value={signature}
                          onChange={setSignature}
                          placeholder={`Best regards,\n\n${currentDisplayName}\nProduct Manager @ Grigora\nwww.grigora.co`}
                          modules={modules}
                          toolbarContainerId="#rich-area-text-effect-settings"
                          className="full-height-pseudo small"
                        />
                      </div>
                      <style>{`
                        .ql-container {
                          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                          font-size: 14px;
                          border: none !important;
                          height: 100%;
                          display: flex;
                          flex-direction: column;
                        }
                        
                        /* Toolbar Container - Light grey rounded container */
                        #rich-area-text-effect-settings {
                          display: flex !important;
                          border: none !important;
                          background-color:rgb(255, 255, 255);
                          padding: 6px 10px !important;
                          flex-wrap: wrap;
                          align-items: center;
                          gap: 0;
                          box-shadow: none;
                        }
                        
                        /* Quill will render toolbar inside the container */
                        #rich-area-text-effect-settings.ql-toolbar,
                        #rich-area-text-effect-settings .ql-toolbar {
                          display: flex !important;
                          border: none !important;
                          background-color: transparent;
                          padding: 0;
                          margin: 0;
                          gap: 0;
                          width: 100%;
                          border-top: 1px solid rgb(243 244 246) !important;
                        }

                        
                        .ql-snow .ql-picker.ql-expanded .ql-picker-options {
                          top: unset !important;
                          bottom: 100%;
                        }
                        .ql-snow.ql-toolbar button {
                          border-radius: 5px !important;
                        }
                        .ql-snow.ql-toolbar button:hover, .ql-snow.ql-toolbar button.ql-active {
                         color: #623ce7 !important;
                          background-color: rgb(243 244 246) !important;
                        }
                        .ql-formats{
                          display: flex !important;
                          flex-wrap: wrap;
                          gap: 10px;
                        }
                        
                        .ql-editor {
                          min-height: 200px;
                          padding: 12px;
                        }
                        
                        .ql-editor.ql-blank::before {
                          color: #9ca3af;
                          font-style: normal;
                        }
                      `}</style>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                      <span className="text-xs text-gray-500">This will be appended to all outgoing emails. HTML formatting is supported.</span>
                  </div>
              </div>
          </div>

          {/* Out of Office */}
          <div className="mb-10 pb-10 border-b border-gray-100">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Out of Office</h3>
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Enable Out of Office Auto-Reply</label>
                  <p className="text-xs text-gray-500 mt-1">Automatically reply to incoming emails with your message.</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={outOfOfficeEnabled}
                    onChange={(e) => setOutOfOfficeEnabled(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-grigora-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-grigora-500"></div>
                </label>
              </div>

              {outOfOfficeEnabled && (
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                      <input
                        type="datetime-local"
                        value={outOfOfficeStartDate}
                        onChange={(e) => {
                          const newStartDate = e.target.value;
                          setOutOfOfficeStartDate(newStartDate);
                          // If end date is before new start date, update end date to be after start date
                          if (outOfOfficeEndDate && newStartDate && outOfOfficeEndDate <= newStartDate) {
                            // Set end date to 1 hour after start date
                            const startTimestamp = datetimeLocalToTimestamp(newStartDate);
                            if (startTimestamp) {
                              const newEndDate = new Date(startTimestamp + 60 * 60 * 1000); // Add 1 hour
                              setOutOfOfficeEndDate(timestampToDatetimeLocal(newEndDate.getTime()));
                            }
                          }
                        }}
                        min={timestampToDatetimeLocal(Date.now())}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-grigora-500 outline-none"
                      />
                      {outOfOfficeStartDate && datetimeLocalToTimestamp(outOfOfficeStartDate) < Date.now() && (
                        <p className="text-xs text-red-500 mt-1">Start date cannot be in the past</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                      <input
                        type="datetime-local"
                        value={outOfOfficeEndDate}
                        onChange={(e) => setOutOfOfficeEndDate(e.target.value)}
                        min={outOfOfficeStartDate || timestampToDatetimeLocal(Date.now())}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-grigora-500 outline-none"
                      />
                      {outOfOfficeEndDate && outOfOfficeStartDate && datetimeLocalToTimestamp(outOfOfficeEndDate) <= datetimeLocalToTimestamp(outOfOfficeStartDate) && (
                        <p className="text-xs text-red-500 mt-1">End date must be after start date</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Auto-Reply Message</label>
                    <textarea
                      value={outOfOfficeMessage}
                      onChange={(e) => setOutOfOfficeMessage(e.target.value)}
                      placeholder="Thank you for your email. I am currently out of the office and will respond to your message when I return."
                      rows={4}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-grigora-500 outline-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">This message will be sent as an automatic reply to incoming emails.</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Import / Export */}
          <div className="mb-10 pb-10 border-b border-gray-100">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Data Management</h3>
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Export Emails</h4>
                  <p className="text-sm text-gray-500 mt-1">Download a copy of your email data.</p>
                </div>
                <Button variant="secondary" icon="download" onClick={() => setActiveModal('export')}>Export Data</Button>
              </div>
              
              <div className="border-t border-gray-200 pt-6 flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-gray-900">Import Emails</h4>
                  <p className="text-sm text-gray-500 mt-1">Import email data from a backup file.</p>
                </div>
                <Button variant="secondary" icon="upload" onClick={() => setActiveModal('import')}>Import Data</Button>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-4">
              <Button variant="ghost" onClick={onClose} disabled={loading}>Cancel</Button>
              <Button onClick={handleSave} disabled={loading}>
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
          </div>
        </div>
      </div>

      {/* MODAL OVERLAY */}
      {activeModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-white">
              <h3 className="text-lg font-bold text-gray-800">
                {activeModal === 'export' ? 'Export Data' : 'Import Data'}
              </h3>
              <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                <span className="material-icons-outlined">close</span>
              </button>
            </div>

            {/* Steps Indicator */}
            <div className="px-12 py-8 flex justify-center">
              <div className="flex items-center w-full max-w-xs relative justify-between">
                {/* Connecting Line */}
                <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-100 -z-10 -translate-y-1/2 rounded-full"></div>
                
                {/* Step 1 */}
                <div className={`relative flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm transition-all duration-300 z-10
                  ${step > 1 ? 'bg-green-500 text-white' : step === 1 ? 'bg-grigora-500 text-white shadow-lg shadow-grigora-500/30 scale-110' : 'bg-white border-2 border-gray-200 text-gray-400'}`}>
                  {step > 1 ? <span className="material-icons-outlined text-lg">check</span> : '1'}
                </div>

                {/* Step 2 */}
                <div className={`relative flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm transition-all duration-300 z-10
                  ${step > 2 ? 'bg-green-500 text-white' : step === 2 ? 'bg-grigora-500 text-white shadow-lg shadow-grigora-500/30 scale-110' : 'bg-white border-2 border-gray-200 text-gray-400'}`}>
                  {step > 2 ? <span className="material-icons-outlined text-lg">check</span> : '2'}
                </div>

                {/* Step 3 */}
                <div className={`relative flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm transition-all duration-300 z-10
                  ${step === 3 ? 'bg-grigora-500 text-white shadow-lg shadow-grigora-500/30 scale-110' : 'bg-white border-2 border-gray-200 text-gray-400'}`}>
                  {'3'}
                </div>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-8 pb-8 pt-2 flex-1 overflow-y-auto">
              
              {/* --- EXPORT CONTENT --- */}
              {activeModal === 'export' && (
                <>
                  {step === 1 && (
                    <div className="space-y-4">
                      <h4 className="text-base font-semibold text-gray-800">Select Folders</h4>
                      <p className="text-sm text-gray-500">Choose which folders you want to include in your export archive.</p>
                      {mailboxes.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <p>Loading mailboxes...</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3 mt-4">
                          {mailboxes.map(mailbox => (
                            <label 
                              key={mailbox.path} 
                              className={`flex items-center p-3 rounded-lg border cursor-pointer transition-all ${selectedFolders.includes(mailbox.path) ? 'border-grigora-500 bg-grigora-50' : 'border-gray-200 hover:bg-gray-50'}`}
                            >
                              <Checkbox
                                checked={selectedFolders.includes(mailbox.path)}
                                onChange={() => toggleFolder(mailbox.path)}
                                className="mr-3"
                              />
                              <span className="material-icons-outlined text-gray-500 mr-2 text-sm">{getFolderIcon(mailbox.path)}</span>
                              <span className="text-sm font-medium text-gray-700">{getFolderName(mailbox.path)}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-6">
                      <div>
                        <h4 className="text-base font-semibold text-gray-800 mb-2">Delivery Destination</h4>
                        <div className="space-y-2">
                          <p className="text-xs text-gray-500">We will send the download link to this email address.</p>
                          <input 
                            type="email" 
                            value={exportEmail} 
                            onChange={(e) => setExportEmail(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-grigora-500 outline-none"
                            placeholder="Enter email address"
                          />
                        </div>
                      </div>

                      <div>
                        <h4 className="text-base font-semibold text-gray-800 mb-2">File Format</h4>
                        <div className="p-4 border border-grigora-200 bg-grigora-50 rounded-lg flex items-center gap-3">
                          <span className="material-icons-outlined text-grigora-600">data_object</span>
                          <div>
                            <div className="text-sm font-bold text-gray-900">JSON Format</div>
                            <div className="text-xs text-gray-500">Standard structured data format.</div>
                          </div>
                          <span className="ml-auto text-xs font-semibold bg-white px-2 py-1 rounded text-grigora-600 border border-grigora-100">Default</span>
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="text-base font-semibold text-gray-800 mb-2">Date Range</h4>
                        <select 
                          value={dateRange} 
                          onChange={(e) => setDateRange(e.target.value)}
                          className="w-full p-2 border border-gray-300 rounded-lg text-sm outline-none focus:border-grigora-500"
                        >
                          <option value="all">All time</option>
                          <option value="last_year">Last 1 year</option>
                          <option value="last_month">Last 30 days</option>
                        </select>
                      </div>

                      <div className="p-3 bg-blue-50 text-blue-800 text-sm rounded-lg flex items-start gap-2">
                        <span className="material-icons-outlined text-base mt-0.5 leading-none">info</span>
                        <p>Exporting {selectedFolders.length} folders to {exportEmail}.</p>
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="flex flex-col items-center justify-center py-6 text-center">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4 text-green-600 animate-bounce">
                        <span className="material-icons-outlined text-3xl">check</span>
                      </div>
                      <h4 className="text-xl font-bold text-gray-900 mb-2">Export Ready!</h4>
                      <p className="text-gray-500 text-sm max-w-xs mx-auto mb-6">We've started processing your export. You will receive an email at <span className="font-semibold">{exportEmail}</span> with the download link shortly.</p>
                      <Button onClick={handleCloseModal}>Done</Button>
                    </div>
                  )}
                </>
              )}

              {/* --- IMPORT CONTENT --- */}
              {activeModal === 'import' && (
                <>
                  {step === 1 && (
                    <div className="space-y-4">
                      <h4 className="text-base font-semibold text-gray-800">Upload Backup File</h4>
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 flex flex-col items-center justify-center text-center hover:bg-gray-50 transition-colors cursor-pointer relative h-48">
                        <input 
                          type="file" 
                          className="absolute inset-0 opacity-0 cursor-pointer"
                          onChange={(e) => e.target.files && setImportFile(e.target.files[0])}
                          accept=".mbox,.json"
                        />
                        {importFile ? (
                          <>
                            <span className="material-icons-outlined text-grigora-500 text-4xl mb-2">description</span>
                            <span className="text-gray-900 font-medium">{importFile.name}</span>
                            <span className="text-gray-400 text-xs mt-1">{(importFile.size / 1024).toFixed(2)} KB</span>
                          </>
                        ) : (
                          <>
                            <span className="material-icons-outlined text-gray-400 text-4xl mb-2">cloud_upload</span>
                            <span className="text-gray-700 font-medium">Click or drag file here</span>
                            <span className="text-gray-400 text-xs mt-1">Supports .MBOX and .JSON files</span>
                          </>
                        )}
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-6">
                      <h4 className="text-base font-semibold text-gray-800">Import Options</h4>
                      
                      <div className="space-y-3">
                        <label className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${importStrategy === 'merge' ? 'border-grigora-500 bg-grigora-50' : 'border-gray-200'}`}>
                          <input type="radio" name="strategy" value="merge" checked={importStrategy === 'merge'} onChange={() => setImportStrategy('merge')} className="mt-1 mr-3 text-grigora-600 focus:ring-grigora-500" />
                          <div>
                            <span className="block text-sm font-bold text-gray-800">Merge with existing folders</span>
                            <span className="block text-xs text-gray-500 mt-1">Emails will be added to your current Inbox, Sent, etc. based on their original location.</span>
                          </div>
                        </label>

                        <label className={`flex items-start p-3 border rounded-lg cursor-pointer transition-all ${importStrategy === 'new_folder' ? 'border-grigora-500 bg-grigora-50' : 'border-gray-200'}`}>
                          <input type="radio" name="strategy" value="new_folder" checked={importStrategy === 'new_folder'} onChange={() => setImportStrategy('new_folder')} className="mt-1 mr-3 text-grigora-600 focus:ring-grigora-500" />
                          <div>
                            <span className="block text-sm font-bold text-gray-800">Import to new folder</span>
                            <span className="block text-xs text-gray-500 mt-1">All emails will be placed in a new folder named "Imported {new Date().toLocaleDateString()}".</span>
                          </div>
                        </label>
                      </div>

                      <div className="p-3 bg-yellow-50 text-yellow-800 text-sm rounded-lg flex items-start gap-2">
                        <span className="material-icons-outlined text-base mt-0.5">warning</span>
                        <p>Importing large files may take a few minutes. Please do not close the window.</p>
                      </div>
                    </div>
                  )}

                  {step === 3 && (
                    <div className="flex flex-col items-center justify-center py-6 text-center">
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4 text-green-600">
                        <span className="material-icons-outlined text-3xl">done_all</span>
                      </div>
                      <h4 className="text-xl font-bold text-gray-900 mb-2">Import Successful!</h4>
                      <p className="text-gray-500 text-sm max-w-xs mx-auto mb-6">Your emails have been imported successfully. You can find them in your folder list.</p>
                      <Button onClick={handleCloseModal}>Done</Button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Footer */}
            {step < 3 && (
              <div className="px-6 py-4 border-t border-gray-100 flex justify-between bg-gray-50 rounded-b-2xl">
                <Button variant="ghost" onClick={step === 1 ? handleCloseModal : () => setStep(step - 1)}>
                  {step === 1 ? 'Cancel' : 'Back'}
                </Button>
                <Button 
                  onClick={step === 2 ? handleProcessAction : () => setStep(step + 1)}
                  disabled={
                    (activeModal === 'export' && step === 1 && selectedFolders.length === 0) ||
                    (activeModal === 'import' && step === 1 && !importFile) ||
                    (activeModal === 'export' && step === 2 && !exportEmail) ||
                    isProcessing
                  }
                >
                  {isProcessing ? 'Processing...' : (step === 2 ? (activeModal === 'export' ? 'Start Export' : 'Start Import') : 'Next')}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

